import { computed, defineComponent, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { ProjectAction, ProjectGetter } from '@/store/project/project.module';
import { Project } from '@/store/project/project.model';
import { EnjoinAction } from '@/store/enjoin/enjoin.module';
import { CancelAction } from '@/store/cancel/cancel.module';
import { AccountGetter } from '@/store/account/account.module';
import { Role } from '@/store/role/role.model';
import ProjectResultComponent from '@/components/project/detail/projectResult/ProjectResult.vue';
import ProjectEnjoinFormComponent from '@/components/project/detail/projectEnjoinForm/ProjectEnjoinForm.vue';
import ProjectCancelFormComponent from '@/components/project/cancel/projectCancelForm/ProjectCancelForm.vue';
import ProjectCancelAttachmentModalComponent from '@/components/project/cancel/projectCancelAttachmentModal/ProjectCancelAttachmentModal.vue';
import ProjectCancelAttachmentFormComponent from '@/components/project/cancel/projectCancelAttachmentForm/ProjectCancelAttachmentForm.vue';
import ProjectCancelImageModelComponent from '@/components/project/cancel/projectCancelImageModal/ProjectCancelImageModal.vue';
import ProjectDetailComponent from '@/components/project/detail/projectDetail/ProjectDetail.vue';
import ProjectProcureComponent from '@/components/project/procure/projectProcure/ProjectProcure.vue';
import ProjectProgressComponent from '@/components/project/progress/projectProgress/ProjectProgress.vue';
import ProjectFinishComponent from '@/components/project/detail/projectFinish/ProjectFinish.vue';

export default defineComponent({
  name: 'ProjectDetailPage',
  components: {
    ProjectResultComponent,
    ProjectEnjoinFormComponent,
    ProjectCancelFormComponent,
    ProjectCancelAttachmentModalComponent,
    ProjectCancelAttachmentFormComponent,
    ProjectCancelImageModelComponent,
    ProjectDetailComponent,
    ProjectProcureComponent,
    ProjectProgressComponent,
    ProjectFinishComponent,
  },
  setup: () => {
    const swal = useSweetAlert();
    const route = useRoute();
    const projectId = computed<number>(() => parseInt(route.params.projectId as string));
    const projectEnjoinForm = ref<InstanceType<typeof ProjectEnjoinFormComponent>>();
    const projectCancelForm = ref<InstanceType<typeof ProjectCancelFormComponent>>();
    const projectCancelAttachmentModel = ref<InstanceType<typeof ProjectCancelAttachmentModalComponent>>();
    const projectCancelAttachmentForm = ref<InstanceType<typeof ProjectCancelAttachmentFormComponent>>();
    const projectCancelImageModal = ref<InstanceType<typeof ProjectCancelImageModelComponent>>();
    const store = useStore();
    const role = computed<Role | undefined>(() => store.getters[AccountGetter.SINGLE_ROLE]);
    const isOrs = computed(() => role.value?.name === 'เจ้าหน้าที่ ส.กกภ.');
    const isMinister = computed(() => role.value?.name === 'ผู้แทน ร.นรม./รมต.นร');
    const isProvince = computed(() => role.value?.name === 'เจ้าหน้าที่ประจำจังหวัด');
    const project = computed<Project>(() => store.getters[ProjectGetter.BY_ID](projectId.value));

    onMounted(() => {
      store.dispatch(ProjectAction.FIND, {
        fiscalYearId: 1,
        id: projectId.value,
      })
        .then(() => {
          store.dispatch(EnjoinAction.FETCH_BY_PROJECT_ID, { projectId: projectId.value });

          if (project.value?.isCanceled) {
            store.dispatch(CancelAction.FIND, { projectId: projectId.value });
          } else {
            store.dispatch(CancelAction.CLEAR);
          }
        });
    });

    const enjoinClick = () => {
      projectEnjoinForm.value?.show();
    };

    const cancelClick = () => {
      if (project.value?.projectStatus.key !== 0) {
        swal.confirm('ยกเลิกโครงการ!', 'คุณต้องการยกเลิกโครงการใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            projectCancelForm.value?.show();
          }
        });
      }
    };

    const editCancelClick = () => {
      projectCancelForm.value?.show();
    };

    const attachmentClick = () => {
      projectCancelAttachmentModel.value?.show();
    };

    const uploadClick = () => {
      Promise.resolve(projectCancelAttachmentModel.value?.hide())
        .then(() => projectCancelAttachmentForm.value?.show());
    };

    const uploadClose = () => {
      Promise.resolve(projectCancelAttachmentForm.value?.hide())
        .then(() => projectCancelAttachmentModel.value?.show());
    };

    const imageClick = () => {
      projectCancelImageModal.value?.show();
    };

    const refresh = () => {
      store.dispatch(ProjectAction.FIND, {
        fiscalYearId: 1,
        id: projectId.value,
      });
    };

    return {
      project,
      projectEnjoinForm,
      projectCancelForm,
      projectCancelAttachmentModel,
      projectCancelAttachmentForm,
      projectCancelImageModal,
      isOrs,
      isMinister,
      isProvince,
      enjoinClick,
      cancelClick,
      editCancelClick,
      attachmentClick,
      uploadClick,
      uploadClose,
      imageClick,
      refresh,
    };
  },
});
